import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Link,
  HStack,
  Input,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { Plural, Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { DownloadIcon, Library } from 'lucide-react';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import { RouterOutputs, trpcTenant } from '../trpcTenant';
import { Spinner } from '@chakra-ui/spinner';
import { CourseKind } from '@duke-hq/svc-api-db';
import { useSnapshot } from 'valtio';
import { quickReferralModalState } from '../tenantState/QuickReferralState';
import {
  CourseReferralModalMode,
  courseReferralModalState,
} from '../tenantState/CourseReferralState';
import { useTenantContext } from '../TenantContext';
import { useState } from 'react';
import PdfGeneratorButton from '@duke-hq/ui/components/PdfGeneratorButton/index';
import { AdminCourseScheduleItemSchema } from '@duke-hq/shared-constants/course';
import { groupItemsIntoSections } from '@duke-hq/ui/components/SectionedList/SectionedList';
import { courseSection } from '@duke-hq/app-provider-web/tenant/Courses/constants';
import { z } from 'zod';

type Course = RouterOutputs['courses']['all'][number];

interface IFormattedCourse {
  title: string;
  code: string;
  description: string;
  semesterPrice: number;
  fullCoursePrice: number;
  kind: string;
  duration: string;
  qualificationKind: string | null;
}

function calculateCourseDuration(course: Course): number {
  const schedule = z.array(AdminCourseScheduleItemSchema).safeParse(course.schedule);
  if (
    schedule.success === false ||
    schedule.data == null ||
    schedule.error === true ||
    schedule.data.length === 0
  ) {
    return 0;
  }

  const getMaxWeek = (semester: string): number => {
    const weeks = schedule.data
      .filter((item) => item.Semester === semester)
      .map((item) => Number(item.Week));
    return weeks.length > 0 ? Math.max(...weeks) : 0;
  };

  const semesterOneWeeks = getMaxWeek('1');
  const semesterTwoWeeks = getMaxWeek('2');
  const total = semesterOneWeeks + semesterTwoWeeks;

  return total;
}

function formatCourses(rawCourses: Course[] | undefined | null): IFormattedCourse[] {
  if (!rawCourses) {
    return [];
  }

  return rawCourses.map((course) => {
    const fullCoursePrice = parseFloat(course.price_aud?.toString() || '0');

    const total = calculateCourseDuration(course);

    return {
      title: course.title ?? '',
      code: course.identifier ?? '',
      description: course.description ?? '',
      semesterPrice: fullCoursePrice / 2,
      fullCoursePrice,
      kind: course.kind,
      duration: `${total} week${total === 1 ? '' : 's'}`,
      qualificationKind: course.qualificationKind,
    };
  });
}

function ModalContent({ onClose }: { onClose: () => void }) {
  const tenantCtx = useTenantContext();
  const [courseSearchValue, setCourseSearchValue] = useState('');

  const { data, isLoading } = trpcTenant.courses.all.useQuery({
    search: courseSearchValue.trim(),
    order: 'category',
  });

  const quickReferralModalSnap = useSnapshot(quickReferralModalState);
  const courseReferralModalSnap = useSnapshot(courseReferralModalState);
  const isTenantUsingNewReferralProcess = tenantCtx?.settings.uses_new_referral_process;

  const pdfData: { courses: IFormattedCourse[] } = {
    courses: formatCourses(data),
  };

  return (
    <VStack w="full" h="636px" justify="start" gap={4}>
      <Box px={7} w="full">
        <HStack justifyContent="space-between" py={4}>
          <Text fontSize="lg" fontWeight="bold">
            <Trans>Course Catalogue</Trans>
          </Text>
          <PdfGeneratorButton
            documentType="courseCatalog"
            label={t`Download Catalogue`}
            data={pdfData}
            variant="outline"
            leftIcon={<DownloadIcon size={16} />}
          />
        </HStack>
        <Input
          value={courseSearchValue}
          onChange={(e) => setCourseSearchValue(e.target.value)}
          placeholder={t`Search Courses`}
        />
      </Box>
      <VStack w="full" h="auto" alignItems="start" px={7} overflow="auto" spacing={4}>
        {isLoading ? (
          <Center pt={8} w="full">
            <Spinner />
          </Center>
        ) : (
          groupItemsIntoSections(data ?? [], courseSection).map((section) => (
            <Box key={section.key} w="full">
              <Text
                fontSize="xs"
                fontWeight="bold"
                color="fg.muted"
                textTransform="uppercase"
                letterSpacing="wide"
                position="sticky"
                top={0}
                zIndex={1}
                bg="bg.surface"
                py={2}
                mb={1}
              >
                {section.label}
              </Text>
              <VStack w="full" alignItems="start" spacing={3}>
                {section.items.map((course: Course) => (
                  <Box key={course.id} p={3} rounded={12} bg="bg.subtle" w="full">
                    <Flex alignItems="start" justifyContent="space-between">
                      <Text fontSize="md" color="fg.accent-emphasis">
                        <Text as="span" fontWeight="semibold">
                          {course.title || 'N/A'}{' '}
                        </Text>
                        {typeof course.specialisation === 'string' &&
                        course.specialisation.trim() !== ''
                          ? ` (${course.specialisation})`
                          : null}
                        {typeof course.identifier === 'string' &&
                        course.identifier.trim() !== '' ? (
                          <Text
                            as="span"
                            fontSize="xs"
                            color="fg.subtle"
                          >{` ${course.identifier}`}</Text>
                        ) : null}
                      </Text>
                      {course.hasWorkPlacement ? (
                        <Badge
                          colorScheme="gray"
                          variant="outline"
                          textTransform="none"
                          color="fg.muted"
                          fontSize="xs"
                          fontWeight="bold"
                          borderRadius="full"
                          flexShrink={0}
                          ml={2}
                        >
                          <Trans>Requires work placement</Trans>
                        </Badge>
                      ) : null}
                    </Flex>
                    {typeof course.description === 'string' && course.description.trim() !== '' ? (
                      <Flex bg="white" p={2} rounded={8} mt={1}>
                        <Text fontSize="xs" fontWeight="medium">
                          {course.description}
                        </Text>
                      </Flex>
                    ) : null}

                    <Flex alignItems="center" mt={3} justifyContent="space-between">
                      <Flex gap={3}>
                        {course.kind === CourseKind.QUALIFICATION ? (
                          <Flex alignItems="center" gap={1.5}>
                            <Text fontSize="2xs" color="fg.subtle" fontWeight="medium">
                              <Trans>Single semester</Trans>
                            </Text>
                            <Text fontSize="md" fontWeight="semibold">
                              ${parseFloat(course.price_aud?.toString() || '0') / 2}
                            </Text>
                          </Flex>
                        ) : null}
                        <Flex alignItems="center" gap={1.5}>
                          <Text fontSize="2xs" color="fg.subtle" fontWeight="medium">
                            <Trans>Full Course</Trans>
                          </Text>
                          <Text fontSize="md" fontWeight="semibold">
                            ${parseFloat(course.price_aud?.toString() || '0')}
                          </Text>
                        </Flex>
                        <Flex alignItems="center" gap={1.5}>
                          <Text fontSize="2xs" color="fg.subtle" fontWeight="medium">
                            <Trans>Course Duration</Trans>
                          </Text>
                          <Text fontSize="2xs" fontWeight="medium">
                            <Plural
                              value={calculateCourseDuration(course)}
                              one="# week"
                              other="# weeks"
                            />
                            {course.hasWorkPlacement ? ` + ${t`Work placement`}` : null}
                          </Text>
                        </Flex>
                      </Flex>
                      <Flex gap={2}>
                        {course?.courseFlyerLink != null ? (
                          <Button
                            as={Link}
                            fontSize="xs"
                            size="2xs"
                            variant="ghost"
                            href={course.courseFlyerLink ?? undefined}
                            bg="bg.surface"
                            color="fg.muted"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Trans>Course Details</Trans>
                          </Button>
                        ) : null}
                        <Button
                          fontSize="xs"
                          size="2xs"
                          variant="outline"
                          onClick={() => {
                            onClose();
                            if (isTenantUsingNewReferralProcess === true) {
                              courseReferralModalSnap.openModal();
                              courseReferralModalSnap.setSelectedCourseAttributes({
                                id: course.id,
                                title: course.title || '',
                                price_aud: parseFloat(course.price_aud?.toString() || '0'),
                                identifier: course.identifier ?? '',
                                specialisation: course.specialisation ?? '',
                                kind: course.kind,
                                status: course.status,
                              });
                              courseReferralModalSnap.setMode(
                                CourseReferralModalMode.CREATE_NEW_FOR_SELECTED_COURSE,
                              );
                            } else {
                              quickReferralModalSnap.openModal();
                              quickReferralModalSnap.setCourseData({
                                id: course.id,
                                title: course.title || '',
                                price_aud: parseFloat(course.price_aud?.toString() || '0'),
                                identifier: course.identifier ?? '',
                                specialisation: course.specialisation ?? '',
                                kind: course.kind,
                                description: course.description ?? '',
                                accredited: course.accredited ?? false,
                              });
                            }
                          }}
                        >
                          <Trans>Create referral</Trans>
                        </Button>
                      </Flex>
                    </Flex>
                  </Box>
                ))}
              </VStack>
            </Box>
          ))
        )}
        {!isLoading && data?.length === 0 ? (
          <Center w="full" py={8}>
            <Text fontSize="md" color="fg.muted">
              <Trans>Course not found</Trans>
            </Text>
          </Center>
        ) : null}
      </VStack>
    </VStack>
  );
}

function CourseCatalogueModal() {
  const { isOpen, onClose, onOpen } = useDisclosure();

  return (
    <FormModalWrapper
      modalOpenButtonText={t`Course Catalogue`}
      modalOpenButtonLeftIcon={Library}
      modalOpenButtonVariant="outline"
      modalContent={<ModalContent onClose={onClose} />}
      isOpen={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      modalContentHeight="700px"
      modalOpenButtonProps={{ minW: 'fit-content' }}
    />
  );
}

export default CourseCatalogueModal;
